import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Car, MessageCircle, Phone, MapPin, Info, Search,
  ArrowRight, Users, Fuel, Settings, Snowflake, Navigation,
} from "lucide-react";
import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";

const BASE_URL = "https://senutour.com/api";

const ONGLETS = [
  { id: "location",  label: "Location de voiture", Icon: Car,        categorie: "Location voiture" },
  { id: "chauffeur", label: "Trouver un chauffeur", Icon: Navigation, categorie: "Chauffeur" },
];

export default function Transport() {
  const navigate = useNavigate();
  const [onglet, setOnglet]           = useState("location");
  const [filtreVille, setFiltreVille] = useState("toutes");
  const [filtreType, setFiltreType]   = useState("tous");
  const [vehicules, setVehicules]     = useState([]);
  const [chargement, setChargement]   = useState(true);
  const [descriptionsOuvertes, setDescriptionsOuvertes] = useState({});

  const categorieActive = ONGLETS.find(o => o.id === onglet).categorie;

  useEffect(() => {
    const fetchVehicules = async () => {
      setChargement(true);
      try {
        const params = new URLSearchParams({ type: categorieActive });
        if (filtreVille !== "toutes") params.append("villeDepart", filtreVille);

        const res = await fetch(`${BASE_URL}/transports?${params}`);
        const data = await res.json();
        setVehicules(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Erreur chargement véhicules:", error);
        setVehicules([]);
      } finally {
        setChargement(false);
      }
    };
    fetchVehicules();
  }, [filtreVille, categorieActive]);

  const vehiculesFiltres = vehicules.filter(v => {
    const matchType = filtreType === "tous" || v.typeVehicule === filtreType;
    return matchType;
  });

  const toggleDescription = (id) => {
    setDescriptionsOuvertes(prev => ({ ...prev, [id]: !prev[id] }));
  };

  // Construit le message pré-rempli envoyé au propriétaire/chauffeur sur WhatsApp.
  // WhatsApp ne permet pas de joindre une image via un lien wa.me — on inclut donc
  // le lien de la photo dans le texte, avec les infos utiles pour répondre vite.
  const buildMessageWhatsapp = (v) => {
    const titre = `${v.marque || ""} ${v.modele || ""}`.trim() || v.nom || "ce véhicule";
    const lignes = [
      `Bonjour, je suis intéressé(e) par le véhicule *${titre}*${v.annee ? ` (${v.annee})` : ""} vu sur SenuTour.`,
      v.typeVehicule ? `Type : ${v.typeVehicule}` : null,
      (v.prixJournee || v.prix) ? `Prix annoncé : ${(v.prixJournee || v.prix).toLocaleString()} F / jour` : null,
      v.villeDepart ? `Ville : ${v.villeDepart}` : null,
      v.description ? `Description : ${v.description}` : null,
      v.images?.[0] ? `Photo : ${v.images[0]}` : null,
      "Est-il toujours disponible ?",
    ].filter(Boolean);
    return lignes.join("\n");
  };

  return (
    <>
      <Navbar />

      <div style={{ fontFamily: "'Space Grotesk', sans-serif", background: "#FAF5EC", minHeight: "100vh", color: "#1C1008" }}>
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,300;0,9..144,700;0,9..144,800;1,9..144,400&family=Space+Grotesk:wght@300;400;500;600;700&display=swap');
          .btn-primary { background: #C4441A; transition: background .2s, transform .2s; border: none; cursor: pointer; }
          .btn-primary:hover { background: #A03614; transform: translateY(-1px); }
          .btn-whatsapp { background: #25D366; transition: background .2s; border: none; cursor: pointer; }
          .btn-whatsapp:hover { background: #1DA851; }
          .vehicule-card { transition: all .25s; }
          .vehicule-card:hover { transform: translateY(-3px); box-shadow: 0 12px 30px rgba(28,16,8,0.12) !important; }
          .onglet-btn { transition: all .2s; cursor: pointer; }
          .onglet-btn:hover { color: #C4441A !important; }
          .voir-plus-btn { transition: color .15s; }
          .voir-plus-btn:hover { color: #A03614 !important; }
          @media (max-width: 767px) {
            .vehicules-grid { grid-template-columns: 1fr !important; }
          }
        `}</style>

        {/* ── HERO ── */}
        <div style={{ background: "linear-gradient(135deg, #1C1008 0%, #2D1A08 100%)", padding: "60px 5% 0", position: "relative", overflow: "hidden" }}>
          <div style={{ position: "absolute", inset: 0, backgroundImage: "radial-gradient(circle,rgba(196,68,26,.07) 1px,transparent 1px)", backgroundSize: "28px 28px" }} />
          <div style={{ maxWidth: 1100, margin: "0 auto", position: "relative", zIndex: 1, paddingBottom: 40 }}>
            <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 3, color: "#C4441A", textTransform: "uppercase", display: "block", marginBottom: 12 }}>Transport & Location</span>
            <h1 style={{ fontFamily: "'Fraunces',serif", fontSize: "clamp(26px,4vw,48px)", fontWeight: 800, color: "white", lineHeight: 1.15, marginBottom: 14, letterSpacing: "-1px" }}>
              Voyagez au Sénégal<br/>
              <span style={{ color: "#E8834A", fontStyle: "italic" }}>en toute liberté</span>
            </h1>
            <p style={{ fontSize: 14, color: "rgba(255,255,255,0.6)", lineHeight: 1.7, maxWidth: 480, marginBottom: 24 }}>
              Louez un véhicule ou contactez directement un chauffeur. Dakar, Saly, Diamniadio — partout au Sénégal pour les JOJ.
            </p>
          </div>

          {/* ── ONGLETS ── */}
          <div style={{ maxWidth: 1100, margin: "0 auto", position: "relative", zIndex: 1, display: "flex", gap: 4 }}>
            {ONGLETS.map(o => (
              <button key={o.id} className="onglet-btn" onClick={() => setOnglet(o.id)}
                style={{
                  background: onglet === o.id ? "#FAF5EC" : "transparent",
                  border: "none", borderRadius: "12px 12px 0 0",
                  padding: "14px 24px", display: "flex", alignItems: "center", gap: 8,
                  fontFamily: "'Space Grotesk',sans-serif", fontSize: 14, fontWeight: 700,
                  color: onglet === o.id ? "#C4441A" : "rgba(255,255,255,0.65)",
                }}>
                <o.Icon size={16} /> {o.label}
              </button>
            ))}
          </div>
        </div>

        <div style={{ maxWidth: 1100, margin: "0 auto", padding: "40px 5% 48px" }}>

          {/* ── FILTRES ── */}
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: 12, marginBottom: 24 }}>
            <div>
              <h2 style={{ fontFamily: "'Fraunces',serif", fontSize: "clamp(20px,3vw,32px)", fontWeight: 800, color: "#1C1008", marginBottom: 6 }}>
                {onglet === "location" ? "Véhicules disponibles" : "Chauffeurs disponibles"}
              </h2>
              <p style={{ fontSize: 14, color: "#7A5A3A", lineHeight: 1.6 }}>
                {onglet === "location"
                  ? "Contactez directement le propriétaire via WhatsApp ou téléphone pour finaliser votre location."
                  : "Contactez directement le chauffeur via WhatsApp ou téléphone pour réserver une course."}
              </p>
            </div>
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
              <select value={filtreVille} onChange={e => setFiltreVille(e.target.value)}
                style={{ padding: "9px 14px", border: "1.5px solid #E8DCC8", borderRadius: 8, fontFamily: "'Space Grotesk',sans-serif", fontSize: 13, color: "#1C1008", background: "white", cursor: "pointer", outline: "none" }}>
                <option value="toutes">Toutes les villes</option>
                <option value="Dakar">Dakar</option>
                <option value="Diamniadio">Diamniadio</option>
                <option value="Saly">Saly</option>
              </select>
              <select value={filtreType} onChange={e => setFiltreType(e.target.value)}
                style={{ padding: "9px 14px", border: "1.5px solid #E8DCC8", borderRadius: 8, fontFamily: "'Space Grotesk',sans-serif", fontSize: 13, color: "#1C1008", background: "white", cursor: "pointer", outline: "none" }}>
                <option value="tous">Tous les types</option>
                <option value="Berline">Berline</option>
                <option value="SUV">SUV</option>
                <option value="Minibus">Minibus</option>
              </select>
            </div>
          </div>

          {/* Bandeau info contact */}
          <div style={{ background: "rgba(196,132,26,0.08)", border: "1.5px solid rgba(196,132,26,0.25)", borderRadius: 12, padding: "14px 20px", marginBottom: 24, display: "flex", alignItems: "center", gap: 12 }}>
            <Info size={20} color="#C4841A" style={{ flexShrink: 0 }} />
            <p style={{ fontSize: 13, color: "#7A5A3A", margin: 0, lineHeight: 1.6 }}>
              {onglet === "location"
                ? "La location de véhicule se fait directement entre vous et le propriétaire. Pensez à vérifier les conditions d'assurance, la validité de votre permis et les responsabilités en cas d'incident avant de finaliser."
                : "La course se négocie directement avec le chauffeur via WhatsApp ou téléphone. Convenez du prix et du trajet avant le départ."}
            </p>
          </div>

          {chargement ? (
            <div style={{ textAlign: "center", padding: "60px 20px", color: "#7A5A3A" }}>Chargement…</div>
          ) : (
            <div className="vehicules-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 20 }}>
              {vehiculesFiltres.map(v => (
                <div key={v.id} className="vehicule-card"
                  style={{ background: "white", borderRadius: 14, border: "2px solid #E8DCC8", overflow: "hidden", boxShadow: "0 2px 12px rgba(28,16,8,0.06)" }}>

                  {/* Photo agrandie pour mieux voir le véhicule */}
                  <div style={{ height: 220, background: v.images?.[0] ? `url(${v.images[0]}) center/cover no-repeat` : "linear-gradient(135deg,#1C1008,#2D1A08)", display: "flex", alignItems: "center", justifyContent: "center", position: "relative" }}>
                    {!v.images?.[0] && <Car size={48} color="rgba(255,255,255,0.3)" />}
                    <span style={{ position: "absolute", top: 10, right: 10, fontSize: 11, fontWeight: 700, padding: "4px 10px", borderRadius: 20, background: v.disponible ? "rgba(61,107,46,0.85)" : "rgba(196,68,26,0.85)", color: "white" }}>
                      {v.disponible ? "Disponible" : (onglet === "location" ? "Loué" : "Indisponible")}
                    </span>
                    <span style={{ position: "absolute", top: 10, left: 10, fontSize: 11, fontWeight: 700, padding: "4px 10px", borderRadius: 20, background: "rgba(255,255,255,0.15)", color: "white" }}>
                      {v.typeVehicule}
                    </span>
                  </div>

                  <div style={{ padding: "16px" }}>
                    <h3 style={{ fontFamily: "'Fraunces',serif", fontSize: 17, fontWeight: 800, color: "#1C1008", marginBottom: 4 }}>
                      {v.marque} {v.modele}
                      <span style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 13, fontWeight: 500, color: "#7A5A3A", marginLeft: 6 }}>{v.annee}</span>
                    </h3>

                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
                      <span style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, background: "#FAF5EC", color: "#7A5A3A", padding: "3px 8px", borderRadius: 20, fontWeight: 600, border: "1px solid #E8DCC8" }}>
                        <Users size={12} /> {v.capacite} places
                      </span>
                      {v.carburant && (
                        <span style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, background: "#FAF5EC", color: "#7A5A3A", padding: "3px 8px", borderRadius: 20, fontWeight: 600, border: "1px solid #E8DCC8" }}>
                          <Fuel size={12} /> {v.carburant}
                        </span>
                      )}
                      {v.boite && (
                        <span style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, background: "#FAF5EC", color: "#7A5A3A", padding: "3px 8px", borderRadius: 20, fontWeight: 600, border: "1px solid #E8DCC8" }}>
                          <Settings size={12} /> {v.boite}
                        </span>
                      )}
                      {v.climatisation && (
                        <span style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, background: "#FAF5EC", color: "#7A5A3A", padding: "3px 8px", borderRadius: 20, fontWeight: 600, border: "1px solid #E8DCC8" }}>
                          <Snowflake size={12} /> Climatisé
                        </span>
                      )}
                    </div>

                    {/* Description tronquée à 2 lignes avec bouton Voir plus / Voir moins */}
                    {v.description && (
                      <div style={{ marginBottom: 10 }}>
                        <p style={{
                          fontSize: 12, color: "#7A5A3A", lineHeight: 1.5, margin: 0,
                          display: descriptionsOuvertes[v.id] ? "block" : "-webkit-box",
                          WebkitLineClamp: descriptionsOuvertes[v.id] ? "unset" : 2,
                          WebkitBoxOrient: "vertical",
                          overflow: descriptionsOuvertes[v.id] ? "visible" : "hidden",
                        }}>
                          {v.description}
                        </p>
                        <button className="voir-plus-btn" onClick={() => toggleDescription(v.id)}
                          style={{ background: "none", border: "none", padding: 0, marginTop: 4, fontSize: 11, fontWeight: 700, color: "#C4441A", cursor: "pointer" }}>
                          {descriptionsOuvertes[v.id] ? "Voir moins" : "Voir plus"}
                        </button>
                      </div>
                    )}

                    {v.adresseParking && (
                      <div style={{ display: "flex", alignItems: "center", gap: 5, marginBottom: 14, fontSize: 12, color: "#7A5A3A" }}>
                        <MapPin size={12} color="#7A5A3A" />
                        {v.adresseParking}
                      </div>
                    )}

                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: 12, borderTop: "1px solid #F2E4C8", marginBottom: 14 }}>
                      <div>
                        <div style={{ fontFamily: "'Fraunces',serif", fontSize: 20, fontWeight: 800, color: "#C4441A" }}>{v.prixJournee?.toLocaleString() || v.prix?.toLocaleString()} F</div>
                        <div style={{ fontSize: 11, color: "#7A5A3A" }}>par jour · F CFA</div>
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: "#1C1008" }}>{v.nomProprietaire}</div>
                        <div style={{ fontSize: 11, color: "#7A5A3A" }}>{onglet === "location" ? "Propriétaire" : "Chauffeur"}</div>
                      </div>
                    </div>

                    {v.disponible ? (
                      <div style={{ display: "flex", gap: 8 }}>
                        <a href={`https://wa.me/${v.whatsapp?.replace(/[\s+]/g, "")}?text=${encodeURIComponent(buildMessageWhatsapp(v))}`} target="_blank" rel="noreferrer" style={{ flex: 1, textDecoration: "none" }}>
                          <button className="btn-whatsapp"
                            style={{ width: "100%", color: "white", borderRadius: 8, padding: "10px 8px", fontFamily: "'Space Grotesk',sans-serif", fontSize: 13, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                            <MessageCircle size={15} /> WhatsApp
                          </button>
                        </a>
                        <a href={`tel:${v.telephone}`} style={{ textDecoration: "none" }}>
                          <button style={{ background: "white", border: "1.5px solid #E8DCC8", borderRadius: 8, padding: "10px 12px", fontSize: 13, fontWeight: 700, color: "#1C1008", cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
                            <Phone size={15} />
                          </button>
                        </a>
                      </div>
                    ) : (
                      <div style={{ textAlign: "center", background: "rgba(196,68,26,0.06)", borderRadius: 8, padding: "10px", fontSize: 13, fontWeight: 700, color: "#C4441A" }}>
                        {onglet === "location" ? "Véhicule actuellement loué" : "Chauffeur indisponible"}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {!chargement && vehiculesFiltres.length === 0 && (
            <div style={{ textAlign: "center", padding: "60px 20px", background: "white", borderRadius: 14, border: "1.5px solid #E8DCC8" }}>
              <Search size={40} color="#B89A7A" style={{ marginBottom: 12 }} />
              <div style={{ fontFamily: "'Fraunces',serif", fontSize: 18, fontWeight: 800, color: "#1C1008", marginBottom: 8 }}>
                {onglet === "location" ? "Aucun véhicule trouvé" : "Aucun chauffeur trouvé"}
              </div>
              <p style={{ fontSize: 13, color: "#7A5A3A" }}>Essayez d'autres filtres, ou revenez bientôt.</p>
            </div>
          )}

          {/* ── CTA LOUEUR ── */}
          <div style={{ background: "linear-gradient(135deg, #1C1008 0%, #2D1A08 100%)", borderRadius: 16, padding: "40px", textAlign: "center", position: "relative", overflow: "hidden", marginTop: 48, marginBottom: 24 }}>
            <div style={{ position: "absolute", inset: 0, backgroundImage: "radial-gradient(circle,rgba(196,68,26,.08) 1px,transparent 1px)", backgroundSize: "24px 24px" }} />
            <div style={{ position: "relative", zIndex: 1 }}>
              <Car size={40} color="white" style={{ marginBottom: 16 }} />
              <h2 style={{ fontFamily: "'Fraunces',serif", fontSize: "clamp(20px,3vw,30px)", fontWeight: 800, color: "white", marginBottom: 10 }}>
                Vous avez un véhicule à louer ?
              </h2>
              <p style={{ fontSize: 14, color: "rgba(255,255,255,0.6)", lineHeight: 1.7, maxWidth: 440, margin: "0 auto 24px" }}>
                Inscrivez-vous comme loueur sur SunuTour et proposez votre véhicule aux touristes pendant les JOJ Dakar 2026.
              </p>
              <button className="btn-primary" onClick={() => navigate("/inscription", { state: { role: "loueur" } })}
                style={{ padding: "14px 32px", color: "white", borderRadius: 10, fontFamily: "'Space Grotesk',sans-serif", fontSize: 15, fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 8, boxShadow: "0 8px 20px rgba(196,68,26,0.40)" }}>
                Devenir loueur <ArrowRight size={16} />
              </button>
            </div>
          </div>

          {/* ── CTA CHAUFFEUR ── */}
          <div style={{ background: "white", borderRadius: 16, padding: "40px", textAlign: "center", border: "1.5px solid #E8DCC8", position: "relative", overflow: "hidden" }}>
            <div style={{ position: "relative", zIndex: 1 }}>
              <Car size={40} color="#C4441A" style={{ marginBottom: 16 }} />
              <h2 style={{ fontFamily: "'Fraunces',serif", fontSize: "clamp(20px,3vw,30px)", fontWeight: 800, color: "#1C1008", marginBottom: 10 }}>
                Devenez chauffeur SunuTour
              </h2>
              <p style={{ fontSize: 14, color: "#7A5A3A", lineHeight: 1.7, maxWidth: 440, margin: "0 auto 24px" }}>
                Rejoignez notre réseau de chauffeurs et proposez vos courses directement aux touristes à travers le Sénégal.
              </p>
              <button className="btn-primary" onClick={() => navigate("/inscription", { state: { role: "chauffeur" } })}
                style={{ padding: "14px 32px", color: "white", borderRadius: 10, fontFamily: "'Space Grotesk',sans-serif", fontSize: 15, fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 8, boxShadow: "0 8px 20px rgba(196,68,26,0.30)" }}>
                S'inscrire comme chauffeur <ArrowRight size={16} />
              </button>
            </div>
          </div>

        </div>
      </div>

      <Footer />
    </>
  );
}